import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import AdminPage from '../../components/admin/AdminPage';
import LoadingCard from '../../components/LoadingCard';
import PropertyDetailCard from '../../components/PropertyDetailCard';
import useAuth from '../../hooks/useAuth';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import useModalState from '../../hooks/useModalState';
import ActivateEntityInvestorModal from '../../components/landlordInvestors/ActivateEntityInvestorModal';
import InvestorProfileCard from '../../components/landlordInvestors/InvestorProfileCard';
import InvestorVisitDetailCard from './components/InvestorVisitDetailCard';
import {
  ExistingInvestorProfileFlags,
  GuestVisitInvestor,
} from './models/guestInvestorModel';
import {
  activateGuestInvestorEndpoint,
  getGuestVisitInvestorDetailEndpoint,
} from './services/guestInvestorService';
import Select from 'react-select';
import useProperList from '../../hooks/useProperList';
import { assignProperPreSaleVisitEndpoint } from './services/visitsInvestorService';
import { wait } from '../../helpers/commonHelpers';
import { ActivateGuestVisits } from '../../models/landlordInvestorModel';
import { VisitPreSaleState } from '../../constants/visitsConstants';
import { UserType } from '../../constants/profileConstants';

const InvestGuestVisitDetail = () => {
  const pageName = 'Detalle de Asesoría';
  const pageParentName = 'Asesorías';
  const { id: guestVisitInvestorId = '' } = useParams();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();
  const [guestVisitInvestor, setGuestVisitInvestor] =
    useState<GuestVisitInvestor>();
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [showActivateGuestModal, toggleActivateGuestModal] = useModalState();
  const globalAlertOptions = {
    success: false,
    warning: true,
    onConfirm: () => setErrorAlert(null),
    title: 'Error',
    text: errorAlert || '',
  };
  const [properId, setProperId] = useState<string | null>(null);
  const { propersOptions, loadPropers } = useProperList();

  const activateGuest = async (payload: ActivateGuestVisits) => {
    //call this ep when is a guest

    const { guestId, investorInfo } = payload;

    const investorDetail: GuestVisitInvestor =
      investorInfo as GuestVisitInvestor;

    let assignProper: boolean = false;

    if (investorDetail.userInfo.type === UserType.GUEST) {
      const { status, data } = await callEndpoint(
        activateGuestInvestorEndpoint(guestId, token),
      );
      if (status === 200) {
        assignProper = true;
      } else {
        const response = fetchResponseAdapter(data);
        const flags: ExistingInvestorProfileFlags = response.data;
        const hasExistingByMail = flags.hasOwnProperty('existsProfile');
        const hasExistingByPhone = flags.hasOwnProperty('existsProfileByPhone');
        const message = `${response.message}
        ${
          hasExistingByMail &&
          flags.existsProfile &&
          '(Correo existente en otros perfiles)'
        }
        ${
          hasExistingByPhone &&
          flags.existsProfileByPhone &&
          '(Número existente en otros perfiles) Verifica con administrador ya que no pueden haber teléfonos repetidos en un mismo tipo de usuarios'
        }`;
        setErrorAlert(message);
        toggleActivateGuestModal();
      }
    } else {
      assignProper = true;
    }

    if (assignProper) {
      toggleActivateGuestModal();
      //assign proper ep
      await wait(2000);
      callEndpoint(
        assignProperPreSaleVisitEndpoint(
          guestVisitInvestorId,
          {
            proper_id: properId,
          },
          token,
        ),
      )
        .then()
        .catch();
      navigate(0);
    }
  };

  const loadGuestVisitInvestors = async () => {
    const { status, data } = await callEndpoint(
      getGuestVisitInvestorDetailEndpoint(guestVisitInvestorId, token),
    );
    const response = fetchResponseAdapter(data);
    if (status === 200) {
      setGuestVisitInvestor(response.data);
    } else {
      setErrorAlert(response.message);
    }
  };
  useEffect(() => {
    loadGuestVisitInvestors().catch((e) => setErrorAlert(e.toString()));
  }, []);

  useEffect(() => {
    if (guestVisitInvestor) {
      loadPropers('for_pre_sale');
      setProperId(
        guestVisitInvestor.visitInfo.proper_initial_selected_id ?? null,
      );
    }
  }, [guestVisitInvestor]);

  if (!guestVisitInvestor || isLoading)
    return <LoadingCard name={pageName} parent_name={pageParentName} />;

  const componentAsignProper = (
    <Card>
      <CardBody>
        <h6 className="heading-small text-muted mt-4 mb-4">
          Seleccionar proper
        </h6>
        <Select
          key={`proper-${propersOptions.length}`}
          placeholder={'Selecciona un proper'}
          options={propersOptions}
          value={propersOptions.find((po) => po.value === properId)}
          onChange={(selected) => {
            if (selected?.value) {
              setProperId(selected.value);
            }
          }}
        />
      </CardBody>
    </Card>
  );

  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      globalAlertOptions={globalAlertOptions}
      footer={
        guestVisitInvestor.visitInfo.visit_status ===
          VisitPreSaleState.PENDING_APPROVAL ||
        (!guestVisitInvestor.visitInfo.visit_proper_selected_id &&
          guestVisitInvestor.visitInfo.visit_status ===
            VisitPreSaleState.SCHEDULED) ? (
          <Button color="primary" onClick={() => toggleActivateGuestModal()}>
            Aceptar asesoría
          </Button>
        ) : undefined
      }
    >
      <Row>
        {/* Profile section */}
        <Col className="order-xl-2 pt-10" xl="4">
          <InvestorProfileCard guestInfo={guestVisitInvestor.userInfo} />
        </Col>
        <Col>
          {/* Property info */}
          <PropertyDetailCard
            cover={guestVisitInvestor.projectInfo.cover || ''}
            address={guestVisitInvestor.projectInfo.address}
            code={guestVisitInvestor.projectInfo.code}
            price={''}
            name={guestVisitInvestor.projectInfo.name}
          />
          {/* Visit detail info */}
          <InvestorVisitDetailCard guestVisit={guestVisitInvestor.visitInfo} />

          {/* Asign proper info */}
          {componentAsignProper}
        </Col>
      </Row>
      <ActivateEntityInvestorModal
        guestId={guestVisitInvestor.userInfo.id}
        isOpen={showActivateGuestModal}
        onToggle={toggleActivateGuestModal}
        onConfirmation={activateGuest}
        bodyText={
          guestVisitInvestor.userInfo.is_registered
            ? 'Al aceptar asignaras el lead al inversionista/propietario encontrado.'
            : 'Al activar el guest aceptas todas las asesorías del mismo. Además vuelves al guest un Inversonista.'
        }
        confirmationText={'Entiendo, continuar'}
        abortText={'No, seguir revisando...'}
        investor={guestVisitInvestor}
      />
    </AdminPage>
  );
};

export default InvestGuestVisitDetail;
